<template>
    <div
        class="customer-card"
        :class="{ selected: `${item._id}_${item.index}` === selectedItemId }"
        @click="$emit('select', item)"
        :item-id="`${item._id}_${item.index}`"
    >
        <span class="index-shortcut-keyboard" v-if="item.index < 10">{{ item.index }}</span>
        <div class="customer-info">
            <div class="customer-header">
                <div class="customer-avatar-wrapper">
                    <ContactAvatar :contact="contactForAvatar" :size="'small'" />
                    <div class="platform-badge" v-if="channelType">
                        <PlatformIcon :platform="channelType" :size="12" :tippy-disabled="true" />
                    </div>
                </div>
            <div class="customer-name-section">
                <a-tooltip :title="customerName" :mouseEnterDelay="0.3">
                    <div class="customer-name-tag">{{ customerName }}</div>
                </a-tooltip>
                </div>
            </div>

            <div class="customer-message" v-if="lastMessage">
                <ContactLastMessage :lastMessage="lastMessage" />
            </div>

            <div class="metas">
                <div class="customer-duration">
                    <i class="material-icons">timer</i>
                    {{ formattedDuration }}
                </div>
                <div
                    class="customer-status"
                    :class="{
                        answered: answerStatus === 'answered',
                        waiting: answerStatus !== 'answered',
                    }"
                >
                    {{
                        answerStatus === "answered"
                            ? $t("report.answered")
                            : $t("message.waitingForReply")
                    }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment/moment";
import { mapState } from "vuex";
import ContactAvatar from "./ContactAvatar.vue";
import ContactLastMessage from "./ContactLastMessage.vue";
import PlatformIcon from "./ChatMessageArea/components/PlatformIcon.vue";

export default Vue.extend({
    name: "CustomerCard",
    components: {
        ContactAvatar,
        ContactLastMessage,
        PlatformIcon,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
        groupId: {
            type: String,
            required: true,
        },
        agentName: {
            type: String,
            required: true,
        },
        selectedItemId: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        customerName() {
            return this.item.visitor?.fullname || this.item.fullname || "Bilinmeyen";
        },
        answerStatus() {
            return this.item.answerStatus || this.item.lastConversation?.answerStatus;
        },
        createdDate() {
            return this.item.createdDate || this.item.lastConversation?.createdDate;
        },
        contactForAvatar() {
            // ContactAvatar component'i için contact objesi oluştur
            return {
                ...this.item.visitor,
                ...this.item,
                fullname: this.customerName,
                lastConversation: this.item.lastConversation || {
                    status: this.item.status,
                    users: this.item.users,
                    connectedUser: this.item.connectedUser,
                },
            };
        },
        channelType() {
            const contact = this.item.visitor || this.item;
            const platform = contact.platform || this.item.platform;
            
            if (platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === (contact.channel || this.item.channel));
                return channel ? channel.type : "notWp";
            }
            return platform || "web";
        },
        channelName() {
            const contact = this.item.visitor || this.item;
            if (contact.platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === contact.channel);
                return channel ? channel.name : "";
            }
            return "";
        },
        lastMessage() {
            return this.item.lastMessage || this.item.lastConversation?.lastMessage || null;
        },
    },
    data() {
        return {
            formattedDuration: "",
            dateInterval: null,
        };
    },
    mounted() {
        this.setupInterval();
    },
    beforeDestroy() {
        this.resetInterval();
    },
    methods: {
        updateFormattedDuration() {
            const createdDate = moment(this.createdDate);
            const duration = moment.duration(moment().diff(createdDate));

            if (duration >= 1000 * 60 * 60) {
                this.formattedDuration = moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
            } else {
                this.formattedDuration = moment.utc(duration.asMilliseconds()).format("mm:ss");
            }
        },
        setupInterval() {
            this.resetInterval();
            this.updateFormattedDuration();
            this.dateInterval = setInterval(() => {
                this.updateFormattedDuration();
            }, 1000);
        },
        resetInterval() {
            if (this.dateInterval) clearInterval(this.dateInterval);
            this.dateInterval = null;
        },
    },
});
</script>

<style scoped lang="scss">
.customer-card {
    flex: 1 1 25%;
    padding: 16px;
    margin-bottom: 10px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 140px;
    height: auto;
    width: 250px;
    max-width: 250px;
    background-color: #ffffff;
    box-shadow: 0 0 10px 0 rgb(0 0 0 / 10%);
    position: relative;
    cursor: pointer;
    user-select: none;

    &.selected {
        border: 2px solid #000;
    }

            .index-shortcut-keyboard {
        position: absolute;
        top: 8px;
        right: 8px;
                font-size: 12px;
                font-weight: bold;
                padding: 2px 5px;
                border-radius: 5px;
                border: 1px solid #ccc;
                width: 20px;
                height: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                color: #000;
                background-color: #f1f1f1;
        z-index: 1;
    }

    .customer-info {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        justify-content: space-between;

        .customer-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 8px;

            .customer-avatar-wrapper {
                position: relative;
                flex-shrink: 0;

                .platform-badge {
                    position: absolute;
                    bottom: -2px;
                    right: -2px;
                    background: white;
                    border: 2px solid white;
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
                }
            }

            .customer-name-section {
                display: flex;
                flex-direction: row;
                align-items: center;
                min-width: 0;
                max-width: 100%;
                flex: 1;

            .customer-name-label {
                font-weight: 600;
                font-size: 14px;
                color: #374151;
            }

            .customer-name-tag {
                font-size: 14px;
                color: #374151;
                font-weight: bold;
                padding: 4px 10px;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: inline-block;
                flex: 1;
                min-width: 0;
            }
            }
        }

        .customer-message {
            margin-bottom: 8px;
            min-height: 20px;
        }

        .metas {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: auto;
        }

        .customer-duration {
            display: flex;
            align-items: center;
            gap: 3px;
            font-size: 14px;
            color: #374151;

            i {
                font-size: 18px;
                color: #374151;
            }
        }

        .customer-status {
            font-weight: bold;
            padding: 4px 10px;
            width: max-content;
            border-radius: 9999px;
            font-size: 12px;

            &.answered {
                background-color: rgb(220 252 231);
                color: rgb(22 163 74);
            }

            &.waiting {
                background-color: rgb(254 226 226);
                color: rgb(239 68 68);
            }
        }
    }
}
</style>
